import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { FaEtsy, FaYoutube, FaInstagram } from 'react-icons/fa';
import ProductLimitationsNotice from '../components/ProductLimitationsNotice';
import AdrenalInsufficiencyResources from '../components/AdrenalInsufficiencyResources';
import SEOHead from '../components/SEOHead';
import JsonLd, { breadcrumbSchema, projectActItemListSchema } from '../components/JsonLd';
import { getLocalizedImage, getLocalizedAlt } from '../utils/getLocalizedImage';
import {
  Download,
  Eye,
  Briefcase,
  HeartPulse,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  Thermometer,
  Play,
  Shield,
  Info,
  Package,
  FileText,
  Link as LinkIcon,
  Ruler,
  Weight,
} from 'lucide-react';

const IMAGES = {
  // Legacy defaults kept only for the openCase interior shot (no FR version).
  // The 5 principal images (lineup, cortopod, mini, plus, assembly) are
  // now resolved dynamically via getLocalizedImage() so the FR version is
  // served on /fr/projet-act while /en/project-act keeps the EN version.
  openCase: '/images/8fylal5d_Project_Act_3-Photoroom.jpg',
};

// Bundle image mapping — resolved via getLocalizedImage(imageKey, language)
const BUNDLE_IMAGE_KEYS = {
  regular: 'projectActRegBundle',
  plus: 'projectActPlusBundle',
  complete: 'projectActFullBundle',
};

const PDF_GUIDES = {
  mini: '/images/tw5urix8_Project_ACT_CortoPod_Mini_Setup_Safety_Guide_final_1_.pdf',
  standard: '/images/ut10yha1_Project_ACT_CortoPod_Standard_Setup_Safety_Guide_final.pdf',
  plus: '/images/u5k4n412_Project_ACT_CortoPod_Plus_Setup_Safety_Guide.pdf',
};

const ETSY_PREORDER = 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=58650544';

const ETSY_LISTINGS = {
  mini: 'https://www.etsy.com/listing/4509234910/',
  cortopod: 'https://www.etsy.com/listing/4509216242/',
  plus: 'https://www.etsy.com/listing/4509200791/',
  bundleRegular: 'https://www.etsy.com/listing/4510224301/',
  bundlePlus: 'https://www.etsy.com/listing/4510229497/',
  bundleComplete: 'https://www.etsy.com/listing/4510256124/',
};

// Easy to extend: add more entries to show additional community videos here.
const COMMUNITY_VIDEOS = [
  {
    youtubeId: '6ikkv_OQ0R4',
    youtubeUrl: 'https://www.youtube.com/watch?v=6ikkv_OQ0R4',
    instagramUrl: 'https://www.instagram.com/reel/DZ-iRaYxxVy/',
    author: 'Vanessa Marie',
    en: {
      title: 'First look at the CortoPod ACT\u2122 lineup',
      intro: 'See the Project ACT\u2122 CortoPod collection in a real community unboxing. Vanessa Marie shares a first look at the kits from the perspective of someone connected to the adrenal insufficiency community.',
    },
    fr: {
      title: 'Premier aper\u00e7u de la gamme CortoPod ACT\u2122',
      intro: 'D\u00e9couvrez la collection CortoPod Project ACT\u2122 dans un v\u00e9ritable d\u00e9ballage communautaire. Vanessa Marie offre un premier aper\u00e7u des kits avec la perspective d\u2019une personne li\u00e9e \u00e0 la communaut\u00e9 de l\u2019insuffisance surr\u00e9nalienne.',
    },
  },
];

const ProjectACTPage = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';

  return (
    <main className="pt-20 sm:pt-24">
      <SEOHead
        routeKey="projectAct"
        language={language}
        title={language === 'fr'
          ? 'Project ACT™ | Étuis pour kits d’hydrocortisone d’urgence | MyGuardPod'
          : 'Project ACT™ | Emergency Hydrocortisone Kit Cases | MyGuardPod'}
        description={language === 'fr'
          ? 'Project ACT™ par MyGuardPod : étuis protecteurs conçus pour aider à transporter, organiser, rendre visibles et identifier les kits d’hydrocortisone injectable d’urgence en insuffisance surrénalienne.'
          : 'Project ACT™ by MyGuardPod: protective cases designed to help carry, organize, make visible and identify emergency hydrocortisone injection kits for adrenal insufficiency preparedness.'}
      />
      <JsonLd data={breadcrumbSchema([{ name: language === 'fr' ? 'Projet ACT' : 'Project ACT', routeKey: 'projectAct' }], language)} />
      <JsonLd data={projectActItemListSchema(language)} />
      {/* Hero */}
      <section className="bg-[#1a1a1a] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-[#E31B23] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
                <HeartPulse className="w-4 h-4" />
                Project ACT™
              </span>
              <h1 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-2">
                Project ACT™
              </h1>
              <p className="font-outfit font-bold text-xl sm:text-2xl text-[#E31B23] mb-6">
                Adrenal Crisis Transport
              </p>
              <p className="font-manrope text-lg text-neutral-300 mb-4">
                {fr
                  ? 'Un syst\u00e8me de transport concret pour les kits d\u2019hydrocortisone injectable d\u2019urgence.'
                  : 'A real-world carry system for emergency hydrocortisone injection kits.'}
              </p>
              <p className="font-manrope text-neutral-400 mb-8">
                {fr
                  ? 'Project ACT\u2122 a \u00e9t\u00e9 cr\u00e9\u00e9 pour am\u00e9liorer la fa\u00e7on dont les kits d\u2019urgence surr\u00e9naliens sont transport\u00e9s, prot\u00e9g\u00e9s, identifi\u00e9s, organis\u00e9s et accessibles dans la vie r\u00e9elle \u2014 \u00e0 la maison, \u00e0 l\u2019\u00e9cole, au travail, en voyage ou en d\u00e9placement.'
                  : 'Project ACT\u2122 was created to improve how adrenal emergency kits are carried, protected, identified, organized, and accessed in real life \u2014 at home, school, work, travel, or on the go.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={ETSY_PREORDER}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-6 py-3 rounded-full font-bold transition-all duration-300 active:scale-95"
                  data-testid="act-preorder-hero"
                >
                  <FaEtsy className="w-5 h-5" />
                  <span>{fr ? 'Voir Project ACT sur Etsy' : 'Shop Project ACT on Etsy'}</span>
                </a>
                <a
                  href="#lineup"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] px-6 py-3 rounded-full font-bold transition-all duration-300"
                  data-testid="act-compare"
                >
                  <span>{fr ? 'Comparer les 3 formats' : 'Compare the 3 formats'}</span>
                  <ChevronDown className="w-5 h-5" />
                </a>
              </div>
              <p className="font-manrope text-xs text-neutral-400 mt-4 max-w-xl leading-relaxed">
                {fr
                  ? '\u00c9tui protecteur seulement. M\u00e9dicament non inclus. Aucun contr\u00f4le de temp\u00e9rature. Respectez les instructions du fabricant du m\u00e9dicament.'
                  : 'Protective carrying case only. Medication not included. No temperature control. Follow the medication manufacturer\u2019s instructions.'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src={getLocalizedImage('projectActLineupHero', language)}
                alt={getLocalizedAlt('projectActLineupHero', language)}
                className="w-full h-auto rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ACT Framework */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="font-manrope text-neutral-600 max-w-3xl mx-auto">
              {fr
                ? 'Project ACT se concentre sur le \u00abdernier m\u00e8tre\u00bb pratique de la pr\u00e9paration \u00e0 la crise surr\u00e9nalienne\u00a0: comment le kit est transport\u00e9, trouv\u00e9, ouvert, identifi\u00e9 et compris dans la vraie vie.'
                : 'Project ACT focuses on the practical \u201clast meter\u201d of adrenal crisis preparedness: how the kit is carried, found, opened, identified, and understood in real life.'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { letter: 'A', en: { t: 'ACCESS', d: 'Critical information at a glance.' }, fr: { t: 'ACCESS', d: 'Information critique en un coup d\u2019\u0153il.' } },
              { letter: 'C', en: { t: 'CARRY', d: 'A real-world carry system for emergency hydrocortisone injection kits.' }, fr: { t: 'CARRY', d: 'Un syst\u00e8me de transport concret pour les kits d\u2019hydrocortisone injectable d\u2019urgence.' } },
              { letter: 'T', en: { t: 'TREAT', d: 'TREAT IN PLACE, following the prescribed emergency plan.' }, fr: { t: 'TREAT', d: 'TREAT IN PLACE, en suivant le plan d\u2019urgence prescrit.' } },
            ].map((card, index) => (
              <motion.div
                key={card.letter}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 text-center border-2 border-transparent hover:border-[#E31B23] transition-all duration-300"
              >
                <div className="w-16 h-16 bg-[#E31B23] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="font-outfit font-black text-3xl text-white">{card.letter}</span>
                </div>
                <h3 className="font-outfit font-bold text-xl text-[#1a1a1a] mb-2">
                  {fr ? card.fr.t : card.en.t}
                </h3>
                <p className="font-manrope text-neutral-600">
                  {fr ? card.fr.d : card.en.d}
                </p>
              </motion.div>
            ))}
          </div>

          <p className="text-center mt-10 font-manrope text-sm text-neutral-500 max-w-3xl mx-auto italic">
            {fr
              ? '\u00AB\u00A0TREAT IN PLACE\u00A0\u00BB fait r\u00e9f\u00e9rence au plan d\u2019urgence prescrit par le professionnel de la sant\u00e9 de l\u2019utilisateur. MyGuardPod ne fournit pas de protocoles m\u00e9dicaux, de formation ni de conseils cliniques.'
              : '\u201cTREAT IN PLACE\u201d refers to the emergency response plan prescribed by the user\u2019s healthcare professional. MyGuardPod does not provide medical protocols, training, or clinical guidance.'}
          </p>
        </div>
      </section>

      {/* Independent Adrenal Insufficiency Resources */}
      <section id="external-adrenal-resources" className="py-16 bg-white scroll-mt-24">
        <AdrenalInsufficiencyResources />
      </section>
      <section id="lineup" className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr ? 'Gamme CortoPod ACT' : 'CortoPod ACT Lineup'}
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* CortoPod Mini */}
            <motion.a
              href={ETSY_LISTINGS.mini}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="group block bg-neutral-50 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              data-testid="act-product-mini"
            >
              <img src={getLocalizedImage('cortopodActMini', language)} alt={getLocalizedAlt('cortopodActMini', language)} className="w-full h-auto" />
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-outfit font-black text-xl text-[#1a1a1a]">CortoPod Mini ACT{'\u2122'}</h3>
                  <span className="font-outfit font-bold text-base text-[#1a1a1a] whitespace-nowrap pt-1">
                    $44.99 <span className="text-xs font-medium text-neutral-500">CAD</span>
                  </span>
                </div>
                <p className="font-manrope text-sm text-[#E31B23] font-medium mb-4">
                  {fr ? 'Format de poche ultra-compact' : 'Ultra-compact pocket format'}
                </p>
                <p className="font-manrope text-sm text-neutral-600 mb-4">
                  {fr
                    ? 'Le CortoPod Mini ACT\u2122 est le format le plus petit et minimal de la gamme Project ACT\u2122. Con\u00e7u pour transporter une v\u00e9ritable option de poche pour les essentiels stricts uniquement.'
                    : 'The CortoPod Mini ACT\u2122 is the smallest and most minimal format in the Project ACT\u2122 lineup. It is designed to carry a true pocket-ready option for the strict essentials only.'}
                </p>
                <ul className="space-y-1.5 mb-4">
                  {(fr ? [
                    '1 fiole de succinate sodique d\u2019hydrocortisone sans bo\u00eete originale',
                    'Seringue 3 mL',
                    'Aiguille(s) s\u00e9par\u00e9e(s)',
                    'Tampon d\u2019alcool',
                    'Petit pansement adh\u00e9sif',
                    'Pas d\u2019emballage de taille compl\u00e8te',
                    'Pas de compartiment pour carte',
                  ] : [
                    '1 hydrocortisone sodium succinate vial without original box',
                    '3 mL syringe',
                    'Separate needle(s)',
                    'Alcohol swab',
                    'Small adhesive bandage',
                    'No full-size packaging',
                    'No card compartment',
                  ]).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 font-manrope text-xs text-neutral-600">
                      <CheckCircle className="w-3.5 h-3.5 text-[#E31B23] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="font-manrope text-sm text-neutral-700 italic border-l-2 border-[#E31B23] pl-3 mb-5">
                  {fr
                    ? 'Mieux vaut un kit minimal avec vous qu\u2019un kit complet laiss\u00e9 derri\u00e8re.'
                    : 'Better a minimal kit with you than a full kit left behind.'}
                </p>
                <div className="grid grid-cols-2 gap-3 bg-white border border-neutral-200 rounded-xl p-3 mb-5">
                  <div className="flex items-start gap-2">
                    <Ruler className="w-4 h-4 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-manrope text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{fr ? 'Taille' : 'Size'}</p>
                      <p className="font-manrope text-xs text-[#1a1a1a] font-semibold leading-tight">{'126 \u00d7 65 \u00d7 35 mm'}</p>
                      <p className="font-manrope text-[11px] text-neutral-500 leading-tight">{'5.0 \u00d7 2.6 \u00d7 1.4 in'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Weight className="w-4 h-4 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-manrope text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{fr ? 'Poids' : 'Weight'}</p>
                      <p className="font-manrope text-xs text-[#1a1a1a] font-semibold leading-tight">100 g</p>
                      <p className="font-manrope text-[11px] text-neutral-500 leading-tight">3.5 oz</p>
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] group-hover:bg-neutral-800 text-white px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 w-full">
                  <FaEtsy className="w-4 h-4" />
                  <span>{fr ? 'Voir sur Etsy' : 'View on Etsy'}</span>
                </span>
              </div>
            </motion.a>

            {/* CortoPod */}
            <motion.a
              href={ETSY_LISTINGS.cortopod}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="group block bg-neutral-50 rounded-2xl overflow-hidden border-2 border-[#E31B23] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              data-testid="act-product-cortopod"
            >
              <img src={getLocalizedImage('cortopodAct', language)} alt={getLocalizedAlt('cortopodAct', language)} className="w-full h-auto" />
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-outfit font-black text-xl text-[#1a1a1a]">CortoPod ACT{'\u2122'}</h3>
                  <span className="font-outfit font-bold text-base text-[#1a1a1a] whitespace-nowrap pt-1">
                    $54.99 <span className="text-xs font-medium text-neutral-500">CAD</span>
                  </span>
                </div>
                <p className="font-manrope text-sm text-[#E31B23] font-medium mb-4">
                  {fr ? 'Format quotidien \u00e9quilibr\u00e9' : 'Balanced everyday format'}
                </p>
                <p className="font-manrope text-sm text-neutral-600 mb-4">
                  {fr
                    ? 'Le CortoPod ACT\u2122 est le format de transport quotidien \u00e9quilibr\u00e9. Con\u00e7u pour transporter les composants essentiels d\u2019un kit d\u2019hydrocortisone injectable d\u2019urgence tout en gardant un setup compact, organis\u00e9 et facile \u00e0 identifier.'
                    : 'The CortoPod ACT\u2122 is the balanced daily-carry format. It is designed to carry the core components of an emergency hydrocortisone injection kit while keeping the setup compact, organized, and easy to identify.'}
                </p>
                <ul className="space-y-1.5 mb-4">
                  {(fr ? [
                    '1 fiole de succinate sodique d\u2019hydrocortisone',
                    'Avec ou sans bo\u00eete originale',
                    'Seringue 3 mL',
                    'Aiguille s\u00e9par\u00e9e',
                    'Zone pour carte d\u2019information d\u2019urgence',
                    'Petits articles de soutien / espace pilules',
                    'Taille \u00e9quilibr\u00e9e pour le transport quotidien',
                  ] : [
                    '1 hydrocortisone sodium succinate vial',
                    'With or without original box',
                    '3 mL syringe',
                    'Separate needle',
                    'Emergency information card area',
                    'Small support items / pill space',
                    'Balanced size for everyday carry',
                  ]).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 font-manrope text-xs text-neutral-600">
                      <CheckCircle className="w-3.5 h-3.5 text-[#E31B23] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-3 bg-white border border-neutral-200 rounded-xl p-3 mb-5">
                  <div className="flex items-start gap-2">
                    <Ruler className="w-4 h-4 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-manrope text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{fr ? 'Taille' : 'Size'}</p>
                      <p className="font-manrope text-xs text-[#1a1a1a] font-semibold leading-tight">{'130 \u00d7 88 \u00d7 52 mm'}</p>
                      <p className="font-manrope text-[11px] text-neutral-500 leading-tight">{'5.1 \u00d7 3.5 \u00d7 2.0 in'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Weight className="w-4 h-4 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-manrope text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{fr ? 'Poids' : 'Weight'}</p>
                      <p className="font-manrope text-xs text-[#1a1a1a] font-semibold leading-tight">150 g</p>
                      <p className="font-manrope text-[11px] text-neutral-500 leading-tight">5.3 oz</p>
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center justify-center gap-2 bg-[#E31B23] group-hover:bg-[#c8171e] text-white px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 w-full">
                  <FaEtsy className="w-4 h-4" />
                  <span>{fr ? 'Voir sur Etsy' : 'View on Etsy'}</span>
                </span>
              </div>
            </motion.a>

            {/* CortoPod Plus */}
            <motion.a
              href={ETSY_LISTINGS.plus}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="group block bg-neutral-50 rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              data-testid="act-product-plus"
            >
              <img src={getLocalizedImage('cortopodActPlus', language)} alt={getLocalizedAlt('cortopodActPlus', language)} className="w-full h-auto" />
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="font-outfit font-black text-xl text-[#1a1a1a]">CortoPod Plus ACT{'\u2122'}</h3>
                  <span className="font-outfit font-bold text-base text-[#1a1a1a] whitespace-nowrap pt-1">
                    $69.99 <span className="text-xs font-medium text-neutral-500">CAD</span>
                  </span>
                </div>
                <p className="font-manrope text-sm text-[#E31B23] font-medium mb-4">
                  {fr ? 'Format \u00e0 capacit\u00e9 \u00e9largie' : 'Expanded capacity format'}
                </p>
                <p className="font-manrope text-sm text-neutral-600 mb-4">
                  {fr
                    ? 'Le CortoPod Plus ACT\u2122 est le format \u00e9largi pour les personnes qui ont besoin de plus d\u2019organisation, plus de capacit\u00e9 et d\u2019un kit d\u2019urgence surr\u00e9nalien plus complet.'
                    : 'The CortoPod Plus ACT\u2122 is the expanded format for people who need more organization, more capacity, and a fuller adrenal emergency kit layout.'}
                </p>
                <ul className="space-y-1.5 mb-4">
                  {(fr ? [
                    'Jusqu\u2019\u00e0 2 fioles de succinate sodique d\u2019hydrocortisone',
                    'Avec ou sans bo\u00eetes originales',
                    'Compartiment seringue jusqu\u2019\u00e0 16,5 cm / 6,5 po',
                    'Plus d\u2019organisation interne',
                    'Zone pour carte d\u2019information d\u2019urgence',
                    'Espace m\u00e9dicaments de soutien / pilules',
                    'Meilleure option pour un setup complet',
                  ] : [
                    'Up to 2 hydrocortisone sodium succinate vials',
                    'With or without original boxes',
                    'Syringe compartment up to 16.5 cm / 6.5 in',
                    'More internal organization',
                    'Emergency information card area',
                    'Support meds / pill space',
                    'Best option when a fuller setup is needed',
                  ]).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 font-manrope text-xs text-neutral-600">
                      <CheckCircle className="w-3.5 h-3.5 text-[#E31B23] mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-3 bg-white border border-neutral-200 rounded-xl p-3 mb-5">
                  <div className="flex items-start gap-2">
                    <Ruler className="w-4 h-4 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-manrope text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{fr ? 'Taille' : 'Size'}</p>
                      <p className="font-manrope text-xs text-[#1a1a1a] font-semibold leading-tight">{'180 \u00d7 93 \u00d7 47 mm'}</p>
                      <p className="font-manrope text-[11px] text-neutral-500 leading-tight">{'7.1 \u00d7 3.7 \u00d7 1.9 in'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Weight className="w-4 h-4 text-[#E31B23] mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-manrope text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{fr ? 'Poids' : 'Weight'}</p>
                      <p className="font-manrope text-xs text-[#1a1a1a] font-semibold leading-tight">200 g</p>
                      <p className="font-manrope text-[11px] text-neutral-500 leading-tight">7.1 oz</p>
                    </div>
                  </div>
                </div>
                <span className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] group-hover:bg-neutral-800 text-white px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 w-full">
                  <FaEtsy className="w-4 h-4" />
                  <span>{fr ? 'Voir sur Etsy' : 'View on Etsy'}</span>
                </span>
              </div>
            </motion.a>
          </div>

          <div className="mt-8 space-y-1 text-center">
            <p className="font-manrope text-xs text-neutral-500">
              {fr ? 'Tous les prix sont affich\u00e9s en CAD.' : 'All prices are shown in CAD.'}
            </p>
            <p className="font-manrope text-xs text-neutral-500">
              {fr
                ? 'Les tailles et poids sont approximatifs et peuvent varier l\u00e9g\u00e8rement avec les mises \u00e0 jour de production.'
                : 'Sizes and weights are approximate and may vary slightly with production updates.'}
            </p>
          </div>
        </div>
      </section>

      {/* Community Unboxing */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-1.5 bg-[#E31B23]/10 text-[#E31B23] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-manrope mb-4">
              {fr ? 'Communaut\u00e9' : 'Community'}
            </div>
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr ? 'D\u00e9ballage communautaire' : 'Community Unboxing'}
            </h2>
          </motion.div>

          <div className="space-y-16">
            {COMMUNITY_VIDEOS.map((video, idx) => {
              const content = fr ? video.fr : video.en;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="grid lg:grid-cols-5 gap-10 items-center"
                >
                  {/* Video — left on desktop, top on mobile (lg:col-span-3) */}
                  <div className="lg:col-span-3 lg:order-1 order-2">
                    <div className="relative aspect-video bg-neutral-100 rounded-2xl overflow-hidden shadow-md">
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0`}
                        title={content.title}
                        loading="lazy"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="absolute inset-0 w-full h-full"
                        data-testid={`community-video-${idx}`}
                      />
                    </div>
                  </div>

                  {/* Text + buttons — right on desktop, top on mobile */}
                  <div className="lg:col-span-2 lg:order-2 order-1">
                    <h3 className="font-outfit font-black text-xl sm:text-2xl text-[#1a1a1a] mb-3 leading-tight">
                      {content.title}
                    </h3>
                    <p className="font-manrope text-base text-neutral-600 leading-relaxed mb-6">
                      {content.intro}
                    </p>

                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6">
                      <a
                        href={video.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] hover:bg-neutral-800 text-white px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 active:scale-95 w-full sm:w-auto"
                        data-testid={`community-youtube-${idx}`}
                      >
                        <FaYoutube className="w-5 h-5" />
                        <span>{fr ? 'Voir la vid\u00e9o compl\u00e8te sur YouTube' : 'Watch the full video on YouTube'}</span>
                      </a>
                      {video.instagramUrl && (
                        <a
                          href={video.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 active:scale-95 w-full sm:w-auto"
                          data-testid={`community-instagram-${idx}`}
                        >
                          <FaInstagram className="w-5 h-5" />
                          <span>{fr ? 'Voir le Reel sur Instagram' : 'Watch the short Reel on Instagram'}</span>
                        </a>
                      )}
                    </div>

                    <div className="space-y-2 border-l-2 border-neutral-200 pl-4">
                      <p className="font-manrope text-sm text-neutral-700 leading-relaxed">
                        {fr
                          ? `Vid\u00e9o communautaire partag\u00e9e avec la permission de ${video.author}. Ce d\u00e9ballage offre un premier aper\u00e7u concret de la collection CortoPod Project ACT\u2122.`
                          : `Community video shared with permission from ${video.author}. This unboxing offers a real-world first look at the Project ACT\u2122 CortoPod collection.`}
                      </p>
                      <p className="font-manrope text-xs text-neutral-500 leading-relaxed">
                        {fr
                          ? 'Les \u00e9tuis MyGuardPod sont con\u00e7us pour faciliter le transport, la visibilit\u00e9, l\u2019organisation et l\u2019identification des kits d\u2019hydrocortisone injectable d\u2019urgence. Les m\u00e9dicaments, fournitures m\u00e9dicales et conseils cliniques ne sont pas inclus.'
                          : 'MyGuardPod cases are designed to support the transport, visibility, organization, and identification of emergency hydrocortisone injection kits. Medication, medical supplies, and clinical guidance are not included.'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Which format should I choose? */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr ? 'Quel format choisir?' : 'Which format should I choose?'}
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                name: 'CortoPod Mini ACT\u2122',
                en: 'You need the smallest possible pocket format and only carry the strict essentials.',
                fr: 'Vous avez besoin du format de poche le plus petit possible et ne transportez que les essentiels stricts.',
              },
              {
                name: 'CortoPod ACT\u2122',
                en: 'You want a balanced everyday kit with emergency information, original packaging when possible, and enough organization for daily carry.',
                fr: 'Vous voulez un kit quotidien \u00e9quilibr\u00e9 avec informations d\u2019urgence, emballage d\u2019origine lorsque possible, et assez d\u2019organisation pour le transport quotidien.',
              },
              {
                name: 'CortoPod Plus ACT\u2122',
                en: 'You need more capacity, more organization, or a fuller adrenal emergency kit setup.',
                fr: 'Vous avez besoin de plus de capacit\u00e9, plus d\u2019organisation, ou d\u2019un setup de kit d\u2019urgence surr\u00e9nalien plus complet.',
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 flex items-start gap-4"
              >
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="w-5 h-5 text-[#E31B23]" />
                </div>
                <div>
                  <p className="font-manrope text-neutral-700">
                    <span className="font-outfit font-bold text-[#1a1a1a]">{fr ? 'Choisissez ' : 'Choose '}{item.name} {fr ? 'si\u00a0:' : 'if:'}</span>{' '}
                    {fr ? item.fr : item.en}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 bg-red-50 border-2 border-[#E31B23]/20 rounded-2xl p-6 text-center">
            <p className="font-manrope text-sm text-neutral-700">
              {fr
                ? 'Lorsque possible, les utilisateurs devraient transporter le m\u00e9dicament dans sa bo\u00eete ou emballage d\u2019origine de la pharmacie. Le CortoPod Mini ACT\u2122 est pour le transport de la fiole seule lorsque la compacit\u00e9 est la priorit\u00e9. Suivez toujours les instructions de conservation et de manipulation fournies avec votre m\u00e9dicament.'
                : 'Whenever possible, users should carry medication in its original box or pharmacy packaging. The CortoPod Mini ACT\u2122 is for vial-only carry when compact size is the priority. Always follow the storage and handling instructions provided with your medication.'}
            </p>
          </div>
        </div>
      </section>

      {/* Attachment System */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-start gap-4 bg-neutral-50 rounded-2xl p-8"
          >
            <LinkIcon className="w-6 h-6 text-[#E31B23] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-outfit font-bold text-lg text-[#1a1a1a] mb-2">
                {fr ? 'Syst\u00e8me d\u2019attache MyGuardPod inclus' : 'Included MyGuardPod Attachment System'}
              </h3>
              <p className="font-manrope text-neutral-600">
                {fr
                  ? 'Chaque \u00e9tui Project ACT\u2122 inclut le syst\u00e8me d\u2019attache MyGuardPod avec une sangle d\u2019identification de m\u00e9dicament d\u2019urgence et un mousqueton.'
                  : 'Each Project ACT\u2122 case includes the MyGuardPod attachment system with an emergency medication identifier strap and carabiner.'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bundle Options */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr ? 'Options de bundles Project ACT\u2122' : 'Project ACT\u2122 Bundle Options'}
            </h2>
            <p className="font-manrope text-neutral-600 max-w-3xl mx-auto mb-4">
              {fr
                ? 'Besoin d\u2019un kit d\u2019urgence plus complet ET d\u2019un backup compact de poche?'
                : 'Need both a fuller emergency kit setup and a compact pocket backup?'}
            </p>
            <p className="font-manrope text-sm text-neutral-500 max-w-3xl mx-auto">
              {fr
                ? 'Les bundles Project ACT\u2122 sont con\u00e7us pour les utilisateurs qui veulent un syst\u00e8me de transport plus flexible. Utilisez un \u00e9tui comme setup principal, un comme option de transport quotidien, et un format compact pour les moments r\u00e9els o\u00f9 la taille est l\u2019obstacle.'
                : 'Project ACT\u2122 bundles are designed for users who want a more flexible carry system. Use one case as a main setup, one as an everyday carry option, and one compact format for real-life moments where size is the barrier.'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[
              {
                imageKey: BUNDLE_IMAGE_KEYS.regular,
                price: '$89.98',
                url: ETSY_LISTINGS.bundleRegular,
                en: {
                  name: 'CortoPod ACT\u2122 + Mini ACT\u2122 Bundle',
                  desc: 'Balanced everyday setup plus a compact pocket backup. Use the standard CortoPod ACT\u2122 as the main daily kit when carrying a more complete setup is possible, and keep the Mini as the ultra-compact option when pocket carry matters.',
                },
                fr: {
                  name: 'CortoPod ACT\u2122 + Mini ACT\u2122 Bundle',
                  desc: 'Setup quotidien \u00e9quilibr\u00e9 plus un backup compact de poche. Utilisez le CortoPod ACT\u2122 standard comme kit quotidien principal et gardez le Mini comme option ultra-compacte quand la taille compte.',
                },
              },
              {
                imageKey: BUNDLE_IMAGE_KEYS.plus,
                price: '$104.98',
                url: ETSY_LISTINGS.bundlePlus,
                en: {
                  name: 'CortoPod Plus ACT\u2122 + Mini ACT\u2122 Bundle',
                  desc: 'Expanded emergency kit setup plus a compact pocket backup. The Plus format offers more capacity and organization, while the Mini provides a minimal pocket-carry option for real-life situations where a larger kit may be left behind.',
                },
                fr: {
                  name: 'CortoPod Plus ACT\u2122 + Mini ACT\u2122 Bundle',
                  desc: 'Setup de kit d\u2019urgence \u00e9largi plus un backup compact de poche. Le format Plus offre plus de capacit\u00e9 et d\u2019organisation, tandis que le Mini fournit une option minimale de poche pour les situations o\u00f9 un plus grand kit serait laiss\u00e9 derri\u00e8re.',
                },
              },
              {
                imageKey: BUNDLE_IMAGE_KEYS.complete,
                price: '$154.97',
                url: ETSY_LISTINGS.bundleComplete,
                en: {
                  name: 'Complete CortoPod ACT\u2122 Bundle',
                  desc: 'Main kit. Daily kit. Backup mini. Includes the CortoPod Plus ACT\u2122, CortoPod ACT\u2122, and CortoPod Mini ACT\u2122 for users who want the full range of carry options.',
                },
                fr: {
                  name: 'Complete CortoPod ACT\u2122 Bundle',
                  desc: 'Kit principal. Kit quotidien. Mini de secours. Inclut le CortoPod Plus ACT\u2122, CortoPod ACT\u2122 et CortoPod Mini ACT\u2122 pour ceux qui veulent la gamme compl\u00e8te d\u2019options de transport.',
                },
              },
            ].map((bundle, index) => {
              const content = fr ? bundle.fr : bundle.en;
              return (
                <motion.a
                  key={index}
                  href={bundle.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                  data-testid={`act-bundle-${index}`}
                >
                  <div className="bg-white p-4">
                    <img
                      src={getLocalizedImage(bundle.imageKey, language)}
                      alt={content.name}
                      className="w-full h-auto object-contain rounded-xl"
                    />
                  </div>
                  <div className="px-6 pb-6 pt-2">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <h3 className="font-outfit font-bold text-lg text-[#1a1a1a]">
                        {content.name}
                      </h3>
                      <span className="font-outfit font-bold text-base text-[#1a1a1a] whitespace-nowrap pt-0.5">
                        {bundle.price} <span className="text-xs font-medium text-neutral-500">CAD</span>
                      </span>
                    </div>
                    <p className="font-manrope text-sm text-neutral-600 mb-6">
                      {content.desc}
                    </p>
                    <span className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] group-hover:bg-neutral-800 text-white px-5 py-3 rounded-full font-bold text-sm transition-all duration-300 w-full">
                      <FaEtsy className="w-4 h-4" />
                      <span>{fr ? 'Voir sur Etsy' : 'View on Etsy'}</span>
                    </span>
                  </div>
                </motion.a>
              );
            })}
          </div>

          <div className="space-y-2 text-center">
            <p className="font-manrope text-xs text-neutral-500">
              {fr ? 'Tous les prix sont affich\u00e9s en CAD.' : 'All prices are shown in CAD.'}
            </p>
            <p className="font-manrope text-xs text-neutral-400">
              {fr
                ? 'Les fournitures m\u00e9dicales montr\u00e9es sur les photos sont des exemples uniquement et ne sont pas incluses.'
                : 'Medical supplies shown in photos are examples only and are not included.'}
            </p>
          </div>
        </div>
      </section>

      {/* Setup & Safety Guides */}
      <section className="py-20 sm:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl tracking-tight text-[#1a1a1a] mb-4">
              {fr ? 'Guides d\u2019installation et de s\u00e9curit\u00e9 Project ACT\u2122' : 'Project ACT\u2122 Setup & Safety Guides'}
            </h2>
            <p className="font-manrope text-neutral-600 max-w-2xl mx-auto">
              {fr
                ? 'T\u00e9l\u00e9chargez le guide d\u2019installation et de s\u00e9curit\u00e9 pour chaque mod\u00e8le CortoPod ACT\u2122. Ces guides expliquent comment organiser l\u2019\u00e9tui, o\u00f9 placer chaque article, et les notes de s\u00e9curit\u00e9 cl\u00e9s pour le transport au quotidien.'
                : 'Download the setup and safety guide for each CortoPod ACT\u2122 model. These guides explain how to organize the case, where each item goes, and the key safety notes for real-world carry.'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {[
              {
                pdf: PDF_GUIDES.mini,
                en: { name: 'CortoPod Mini ACT\u2122', subtitle: 'Minimal pocket kit for essential emergency hydrocortisone carry.', btn: 'Download Mini Guide' },
                fr: { name: 'CortoPod Mini ACT\u2122', subtitle: 'Kit de poche minimal pour le transport essentiel d\u2019hydrocortisone d\u2019urgence.', btn: 'T\u00e9l\u00e9charger le guide Mini' },
              },
              {
                pdf: PDF_GUIDES.standard,
                en: { name: 'CortoPod ACT\u2122', subtitle: 'Standard layout with emergency card holder, vial space, syringe space, and support medication compartment.', btn: 'Download Standard Guide' },
                fr: { name: 'CortoPod ACT\u2122', subtitle: 'Configuration standard avec porte-carte d\u2019urgence, espace fiole, espace seringue et compartiment m\u00e9dicaments de soutien.', btn: 'T\u00e9l\u00e9charger le guide Standard' },
              },
              {
                pdf: PDF_GUIDES.plus,
                en: { name: 'CortoPod Plus ACT\u2122', subtitle: 'Expanded format for larger emergency hydrocortisone kits with more organization.', btn: 'Download Plus Guide' },
                fr: { name: 'CortoPod Plus ACT\u2122', subtitle: 'Format \u00e9largi pour les kits d\u2019hydrocortisone d\u2019urgence plus grands avec plus d\u2019organisation.', btn: 'T\u00e9l\u00e9charger le guide Plus' },
              },
            ].map((guide, index) => {
              const content = fr ? guide.fr : guide.en;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-sm flex flex-col"
                >
                  <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                    <FileText className="w-6 h-6 text-[#E31B23]" />
                  </div>
                  <h3 className="font-outfit font-bold text-xl text-[#1a1a1a] mb-2">{content.name}</h3>
                  <p className="font-manrope text-sm text-neutral-600 mb-6 flex-1">{content.subtitle}</p>
                  <div
                    className="inline-flex items-center justify-center gap-2 bg-neutral-200 text-neutral-500 px-5 py-3 rounded-full font-bold text-sm cursor-not-allowed"
                    aria-disabled="true"
                    data-testid={`act-guide-${index}`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>{fr ? 'Guide en cours de mise \u00e0 jour' : 'Updated guide coming soon'}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <p className="font-manrope text-xs text-neutral-500 text-center max-w-3xl mx-auto">
            {fr
              ? 'Ces guides sont des ressources d\u2019installation et d\u2019organisation uniquement. Les fournitures m\u00e9dicales montr\u00e9es sont des exemples et ne sont pas incluses. Suivez toujours le plan d\u2019urgence de votre professionnel de la sant\u00e9, l\u2019\u00e9tiquette de prescription, les instructions du fabricant du m\u00e9dicament et les exigences locales.'
              : 'These guides are setup and organization resources only. Medical supplies shown are examples and are not included. Always follow your healthcare provider\u2019s emergency plan, prescription label, medication manufacturer instructions, and local requirements.'}
          </p>
        </div>
      </section>

      {/* Lid Card Holder Assembly */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={getLocalizedImage('cardHolder', language)}
                alt={getLocalizedAlt('cardHolder', language)}
                className="rounded-2xl shadow-lg w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-outfit font-black text-2xl sm:text-3xl tracking-tight text-[#1a1a1a] mb-2">
                {fr ? 'Assemblage du support de carte' : 'Card Holder Assembly'}
              </h2>
              <p className="font-manrope text-sm text-neutral-500 mb-6">
                {fr
                  ? 'Le support de carte est compos\u00e9 de quatre couches simples\u00a0: cadre avant, feuille transparente, carte d\u2019information d\u2019urgence et dos de retenue.'
                  : 'The card holder is made of four simple layers: front frame, clear sheet, emergency information card, and retaining back.'}
              </p>
              <p className="font-manrope text-neutral-600 mb-2">
                {fr
                  ? 'Ordre d\u2019assemblage\u00a0: Cadre \u2192 Feuille transparente \u2192 Carte \u2192 Dos de retenue.'
                  : 'Assembly order: Frame \u2192 Clear sheet \u2192 Card \u2192 Retaining back.'}
              </p>
              <p className="font-manrope text-sm text-neutral-500 mb-6">
                {fr
                  ? 'Assurez-vous que le c\u00f4t\u00e9 imprim\u00e9 de la carte d\u2019urgence fait face \u00e0 la feuille transparente pour qu\u2019elle reste lisible \u00e0 travers le cadre.'
                  : 'Make sure the printed side of the emergency card faces the clear sheet so it remains readable through the frame.'}
              </p>
              <ol className="space-y-3">
                {(fr ? [
                  'Placez la feuille transparente dans le cadre.',
                  'Placez la carte d\u2019information d\u2019urgence derri\u00e8re la feuille transparente.',
                  'Appuyez sur le dos de retenue en place.',
                  'Ins\u00e9rez le support assembl\u00e9 dans la section du couvercle marqu\u00e9e PULL FOR INFO.',
                  'V\u00e9rifiez que la carte est droite, lisible et bien fix\u00e9e.',
                ] : [
                  'Place the clear sheet into the frame.',
                  'Place the emergency information card behind the clear sheet.',
                  'Press the retaining back into place.',
                  'Insert the assembled holder into the lid section marked PULL FOR INFO.',
                  'Check that the card is upright, readable, and secure.',
                ]).map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-[#E31B23] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <span className="font-manrope text-neutral-700">{step}</span>
                  </li>
                ))}
              </ol>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Fit Check */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-red-50 border-2 border-[#E31B23]/20 rounded-2xl p-8"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-[#E31B23] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-outfit font-bold text-lg text-[#1a1a1a] mb-3">
                  {fr ? 'V\u00e9rification d\u2019ajustement importante' : 'Important Fit Check'}
                </h3>
                <p className="font-manrope text-neutral-700">
                  {fr
                    ? 'Avant de compter sur le kit, confirmez que votre fiole, seringue, aiguille, carte et articles de support s\u2019ins\u00e8rent sans \u00eatre forc\u00e9s, pli\u00e9s, \u00e9cras\u00e9s ou mis sous pression. L\u2019\u00e9tui doit se fermer normalement sans comprimer le m\u00e9dicament ni les fournitures m\u00e9dicales.'
                    : 'Before relying on the kit, confirm that your vial, syringe, needle, card, and support items fit without being forced, bent, crushed, or placed under pressure. The case should close normally without compressing medication or medical supplies.'}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Official Setup Videos Coming Soon */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 bg-neutral-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Play className="w-8 h-8 text-neutral-400" />
            </div>
            <h2 className="font-outfit font-bold text-2xl text-[#1a1a1a] mb-4">
              {fr ? 'Vid\u00e9os d\u2019installation officielles \u2014 Bient\u00f4t disponibles' : 'Official Setup Videos \u2014 Coming Soon'}
            </h2>
            <p className="font-manrope text-neutral-600 max-w-xl mx-auto">
              {fr
                ? 'Des vid\u00e9os d\u2019installation officielles \u00e9tape par \u00e9tape sont actuellement en d\u00e9veloppement. Elles montreront comment organiser chaque format Project ACT pour le transport quotidien et la pr\u00e9paration aux urgences.'
                : 'Official step-by-step setup videos are currently in development. These will show how to organize each Project ACT format for everyday carry and emergency readiness.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Safety Notes */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[#1a1a1a] rounded-2xl p-8"
          >
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-[#E31B23] flex-shrink-0 mt-1" />
              <div className="space-y-3">
                <h3 className="font-outfit font-bold text-lg text-white">
                  {fr ? 'Notes de s\u00e9curit\u00e9 importantes' : 'Important Safety Notes'}
                </h3>
                {(fr ? [
                  'Les produits MyGuardPod sont des \u00e9tuis de transport protecteurs.',
                  'Ils n\u2019administrent, ne dosent, ne surveillent ni ne modifient aucun m\u00e9dicament, et aucune approbation, autorisation ou certification comme dispositif m\u00e9dical n\u2019est revendiqu\u00e9e.',
                  'Les fournitures m\u00e9dicales montr\u00e9es sur les photos sont des exemples uniquement et ne sont pas incluses.',
                  'MyGuardPod ne fournit pas de protocoles m\u00e9dicaux, de formation ni de conseils cliniques.',
                  'Suivez toujours le plan d\u2019urgence de votre professionnel de la sant\u00e9, l\u2019\u00e9tiquette de prescription, les instructions du fabricant du m\u00e9dicament et les exigences locales.',
                  'Ne forcez pas les articles dans l\u2019\u00e9tui. Confirmez que l\u2019\u00e9tui se ferme normalement sans comprimer le m\u00e9dicament ni les fournitures m\u00e9dicales.',
                  '\u00c9vitez l\u2019exposition prolong\u00e9e au soleil direct, aux v\u00e9hicules, aux temp\u00e9ratures de cong\u00e9lation ou \u00e0 la chaleur excessive, \u00e0 moins que cela ne corresponde aux instructions de conservation fournies avec votre m\u00e9dicament.',
                ] : [
                  'MyGuardPod products are protective carrying cases.',
                  'They do not administer, dose, monitor or modify medication, and no medical-device approval, authorization or certification is claimed.',
                  'Medical supplies shown in photos are examples only and are not included.',
                  'MyGuardPod does not provide medical protocols, training, or clinical guidance.',
                  'Always follow your healthcare provider\u2019s emergency plan, prescription label, medication manufacturer instructions, and local requirements.',
                  'Do not force items into the case. Confirm the case closes normally without compressing medication or medical supplies.',
                  'Avoid prolonged exposure to direct sun, vehicles, freezing temperatures, or excessive heat unless this matches the storage instructions provided with your medication.',
                ]).map((note, i) => (
                  <p key={i} className="font-manrope text-sm text-neutral-300">{note}</p>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Full Limitations Notice */}
      <section className="py-12 bg-white border-t border-neutral-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProductLimitationsNotice />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-24 bg-[#E31B23]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-4">
            {fr ? 'Procurez-vous votre CortoPod ACT\u2122' : 'Get Your CortoPod ACT\u2122'}
          </h2>          <p className="font-manrope text-lg text-red-100 mb-8">
            MyGuardPod™ — {fr ? 'Prot\u00e9gez ce qui vous prot\u00e8ge.' : 'Protect what protects you.'}
          </p>
          <a
            href={ETSY_PREORDER}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-[#E31B23] hover:bg-neutral-100 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 active:scale-95"
            data-testid="act-etsy-bottom"
          >
            <FaEtsy className="w-5 h-5" />
            <span>{fr ? 'Voir sur Etsy' : 'Shop on Etsy'}</span>
          </a>
        </div>
      </section>

      {/* 
        FUTURE: Call to ACTion — Share your ACT setup
        Reserved for post-launch phase.
        - Moderated submission feature (not open blog)
        - No live public posting or comments
        - Submissions reviewed before sharing
        - Users must hide personal info before submitting
        - Placeholder: "Share your kit. Help others prepare."
      */}
    </main>
  );
};

export default ProjectACTPage;
